// What a chat runs with (agent, model, effort, working directory) and what a
// device offers, read from the device's reported capabilities and settings.
//
// Promise:
// - A new chat starts from the device's defaults, clamped to what the device
//   says it has: an agent it has, a model of that agent, the model's default
//   effort, and the default directory only while it is allowed and exists.
// - A value the device no longer offers is kept and shown as such (never
//   silently replaced).
// - A new chat goes by default to a device whose agent has reported what it
//   has, never to one that has not connected while another has.
// - A device whose host brings its agent up by itself takes a new chat before
//   its agent reports (`deviceComing`): the chat starts from the device's
//   settings, its default directory included.
import type {
  AgentChatDTO,
  AgentDeviceDTO,
} from "@trameria/core/agent-chat-shared";
import {
  type AgentCapability,
  type Capabilities,
  DEVICE_SETTINGS_DEFAULT,
  type DeviceSettings,
  findWorkdirFor,
  type ModelOption,
  type OtherMcpMode,
  type Workdir,
} from "@trameria/shared-agent";

/**
 * The device a new chat runs on when none is named, by the server's rule:
 * this PC, else the one seen last, else the oldest. (The list comes oldest
 * first; the first one is often a machine that is long gone.)
 */
export function defaultDevice(
  devices: readonly AgentDeviceDTO[] | undefined,
): AgentDeviceDTO | null {
  if (!devices || devices.length === 0) return null;
  const thisPc = devices.find((d) => d.kind === "this_pc");
  if (thisPc) return thisPc;
  let best: AgentDeviceDTO | null = null;
  for (const d of devices)
    if (d.seenAt && (!best?.seenAt || d.seenAt > best.seenAt)) best = d;
  return best ?? devices[0];
}

export function capabilitiesOf(
  device: AgentDeviceDTO | null | undefined,
): Capabilities {
  const c = (device?.capabilities ?? {}) as Partial<Capabilities>;
  return {
    agents: Array.isArray(c.agents) ? c.agents : [],
    workdirs: Array.isArray(c.workdirs) ? c.workdirs : [],
    missingCwds: Array.isArray(c.missingCwds) ? c.missingCwds : [],
  };
}

export function settingsOf(
  device: AgentDeviceDTO | null | undefined,
): DeviceSettings {
  return {
    ...DEVICE_SETTINGS_DEFAULT,
    ...((device?.settings ?? {}) as Partial<DeviceSettings>),
  };
}

export interface RunChoice {
  agentKind: string | null;
  model: string | null;
  effort: string | null;
  cwd: string | null;
  /** The other MCP servers registered on the device: none, read only, or all. */
  otherMcp: OtherMcpMode;
}

export const agentOf = (
  caps: Capabilities,
  kind: string | null,
): AgentCapability | null => caps.agents.find((a) => a.kind === kind) ?? null;

export const modelOf = (
  agent: AgentCapability | null,
  id: string | null,
): ModelOption | null => agent?.models.find((m) => m.id === id) ?? null;

/** Whether a directory may be used now (under an allowed root, not missing). */
export function cwdUsable(caps: Capabilities, cwd: string | null): boolean {
  if (cwd === null) return true;
  return (
    findWorkdirFor(cwd, caps.workdirs) !== null &&
    !caps.missingCwds.includes(cwd)
  );
}

/**
 * Whether a device can take a new chat now: its agent has reported at least
 * one CLI. A device made on the server but never connected (a converted one,
 * or one whose agent was never started) has reported nothing.
 */
export function deviceReady(device: AgentDeviceDTO | null | undefined) {
  return capabilitiesOf(device).agents.length > 0;
}

/**
 * A device whose host brings its agent up by itself (a cloud sandbox, made
 * just now) and whose agent has not reported yet: a new chat is taken and
 * waits for it. Never this PC.
 */
export function deviceComing(device: AgentDeviceDTO | null | undefined) {
  return device?.acceptsBeforeReport === true && !deviceReady(device);
}

/** Whether a new chat can be posted to a device: it is ready, or coming. */
export function deviceTakesChats(device: AgentDeviceDTO | null | undefined) {
  return deviceReady(device) || deviceComing(device);
}

/**
 * The device a new chat goes to unless the person picks one. `keep` is the
 * one decided before: it stays while it is ready (or nothing is), so the
 * choice does not move under the person when the lists are read again.
 * Otherwise: this PC, then the ready device of the most recently updated
 * chat, then the ready device seen last, then `defaultDevice` among all.
 */
export function newChatDevice(
  devices: readonly AgentDeviceDTO[],
  chats: readonly Pick<AgentChatDTO, "deviceId" | "updatedAt">[],
  keep: string | null = null,
): AgentDeviceDTO | null {
  const anyReady = devices.some(deviceReady);
  const kept = devices.find((d) => d.id === keep);
  if (kept && (deviceReady(kept) || !anyReady)) return kept;
  const thisPc = devices.find((d) => d.kind === "this_pc");
  if (thisPc) return thisPc;
  const lastUsed = [...chats]
    .filter((c) => c.deviceId !== null)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .map((c) => devices.find((d) => d.id === c.deviceId))
    .find((d) => d !== undefined && deviceReady(d));
  return (
    lastUsed ??
    defaultDevice(devices.filter(deviceReady)) ??
    defaultDevice(devices)
  );
}

/** The choice a new chat starts with on this device. */
export function defaultChoice(device: AgentDeviceDTO | null): RunChoice {
  const caps = capabilitiesOf(device);
  const settings = settingsOf(device);
  const agent = agentOf(caps, settings.agent) ?? caps.agents[0] ?? null;
  const model =
    modelOf(agent, settings.model) ??
    modelOf(agent, agent?.defaultModel ?? null) ??
    null;
  return {
    agentKind: agent?.kind ?? settings.agent,
    model: model?.id ?? (agent ? null : settings.model),
    effort: model?.defaultEffort ?? null,
    // A coming device has reported no directories to check against; its
    // host set its default (the server takes it the same way).
    cwd:
      settings.cwd !== null &&
      (deviceComing(device) || cwdUsable(caps, settings.cwd))
        ? settings.cwd
        : null,
    // Always "all" for now: there is no case for a chat without the other
    // MCP servers, and the choice is not shown (the person, 2026-11-01).
    // It comes back if the secretary needs to limit them.
    otherMcp: "all",
  };
}

/** The choice after picking another model: its default effort comes with it. */
export function withModel(
  caps: Capabilities,
  choice: RunChoice,
  modelId: string | null,
): RunChoice {
  const model = modelOf(agentOf(caps, choice.agentKind), modelId);
  return { ...choice, model: modelId, effort: model?.defaultEffort ?? null };
}

/** The choice after picking another agent: its default model and effort. */
export function withAgent(
  caps: Capabilities,
  choice: RunChoice,
  kind: string,
): RunChoice {
  const agent = agentOf(caps, kind);
  const model = modelOf(agent, agent?.defaultModel ?? null);
  return {
    ...choice,
    agentKind: kind,
    model: model?.id ?? null,
    effort: model?.defaultEffort ?? null,
  };
}

/** A directory's name as the person reads it: relative to its root. */
export function cwdLabel(workdirs: readonly Workdir[], cwd: string): string {
  const root = findWorkdirFor(cwd, workdirs);
  if (!root) return cwd;
  return cwd === root.path
    ? root.name
    : `${root.name}/${cwd.slice(root.path.length + 1)}`;
}

/** The directories to offer: the roots, their marked children, then recent ones. */
export function cwdOptions(
  caps: Capabilities,
  settings: DeviceSettings,
): { path: string; label: string; note: string | null }[] {
  const out: { path: string; label: string; note: string | null }[] = [];
  const seen = new Set<string>();
  const add = (path: string, note: string | null) => {
    if (seen.has(path)) return;
    seen.add(path);
    out.push({ path, label: cwdLabel(caps.workdirs, path), note });
  };
  for (const w of caps.workdirs) add(w.path, w.path);
  for (const p of settings.recentCwds) if (cwdUsable(caps, p)) add(p, p);
  for (const w of caps.workdirs)
    for (const c of w.children)
      if (c.markers.includes("agents_md") || c.markers.includes("skills"))
        add(`${w.path}/${c.name}`, `${w.path}/${c.name}`);
  return out;
}
